import { useState } from "react";
import { AddCareerInterest } from "./AddCareerInterest";
import {
  QUERY_KEY_FOR_CAREER_INTEREST,
  useGetCareerInterest,
} from "features/settings/hooks/useGetCareerInterest";
import Popconfirm from "antd/es/popconfirm";
import { useDelete } from "hooks/useDelete";
import { Skeleton, Form, Input } from "antd";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { useNavigate } from "react-router-dom";
import {
  getNextSection,
  isLastSection,
} from "../../services/profileNavigationService";
import { useCreateCareerInterest } from "features/settings/hooks/useCreateCareerInterest";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { useGetUserInfo } from "hooks/useGetUserInfo";

const deleteEndPointUrl = "delete-career-interest/";

export const CareerInterest = () => {
  const [addInterest, setAddInterest] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, userInfo } = useGetUserInfo();
  const { data, isLoading } = useGetCareerInterest();
  const { mutate: createInterest, isLoading: isCreating } =
    useCreateCareerInterest();
  const { removeData } = useDelete({
    queryKey: QUERY_KEY_FOR_CAREER_INTEREST,
    deleteEndPointUrl,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSaveAndNext = async () => {
    try {
      const values = await form.validateFields().catch(() => ({}));
      const interestName = values.career_interest;

      if (interestName && interestName.trim() !== "") {
        setIsSubmitting(true);

        createInterest(
          {
            name: interestName,
            talent_id: userInfo?.talent?.id,
            token: token || "",
          },
          {
            onError: (err: any) => {
              openNotification({
                title: "Unable to Save",
                state: "error",
                description:
                  "We couldn't save your career interest right now. Please try again later.",
                duration: 4.5,
              });

              setIsSubmitting(false);
            },
            onSuccess: () => {
              openNotification({
                title: "Success",
                state: "success",
                description: "Career interest added successfully",
                duration: 4.5,
              });

              form.resetFields();

              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_FOR_CAREER_INTEREST],
              });

              setIsSubmitting(false);

              const nextSection = getNextSection(window.location.pathname);
              if (nextSection) {
                navigate(nextSection.link);
              }
            },
          },
        );
      } else {
        const nextSection = getNextSection(window.location.pathname);
        if (nextSection) {
          navigate(nextSection.link);
        }
      }
    } catch (errorInfo) {
      const nextSection = getNextSection(window.location.pathname);
      if (nextSection) {
        navigate(nextSection.link);
      }
    }
  };

  const handleNextWithoutSave = () => {
    const nextSection = getNextSection(window.location.pathname);
    if (nextSection) {
      navigate(nextSection.link);
    }
  };

  const isLast = isLastSection(window.location.pathname);

  return (
    <>
      <AddCareerInterest
        open={addInterest}
        handleClose={() => setAddInterest(false)}
      />

      <div className="mt-6 max-w-5xl">
        <h4 className="text-sm font-semibold text-primary mb-4">
          Add your career interest (Optional)
        </h4>

        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <Skeleton active loading={isLoading}>
            <div className="flex flex-wrap gap-3 mb-4">
              {data?.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 bg-indigo-50 text-primary text-sm font-medium px-3 py-1 rounded-md"
                >
                  <span>{item.name}</span>

                  <Popconfirm
                    okButtonProps={{
                      style: {
                        backgroundColor: "#f5222d",
                        color: "#ffffff",
                      },
                    }}
                    title={`Delete ${item.name}`}
                    description={`Are you sure to delete ${item.name}?`}
                    onConfirm={() => removeData(item.id as unknown as number)}
                  >
                    <i className="ri-delete-bin-line text-gray-400 cursor-pointer text-xl hover:text-red-600"></i>
                  </Popconfirm>
                </div>
              ))}
            </div>
          </Skeleton>

          <Form form={form} layout="vertical">
            <Form.Item
              name="career_interest"
              // Removed required rule
              rules={[
                {
                  min: 2,
                  message: "Career interest must be at least 2 characters",
                },
              ]}
            >
              <Input
                type="text"
                placeholder="e.g work life balance (optional)"
                className="w-full border border-gray-200 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                onPressEnter={handleSaveAndNext}
              />
            </Form.Item>
          </Form>

          <button
            onClick={() => setAddInterest(true)}
            className="border border-primary text-primary text-sm font-medium px-4 py-2 rounded-md flex items-center gap-2 hover:bg-primary hover:text-white transition mt-4"
          >
            <i className="ri-add-line"></i>
            Add More Career Interests
          </button>

          <div className="mt-8 pt-4 border-t">
            <ProfileNavigationButtons
              hasForm={true}
              onSave={handleSaveAndNext}
              onNext={handleNextWithoutSave}
              isLoading={isCreating}
              isSubmitting={isSubmitting}
              nextButtonLabel={isLast ? "Complete Profile" : "Next"}
            />
          </div>
        </div>
      </div>
    </>
  );
};
